// Native WebSocket transport for the zeno feed: exponential-backoff
// reconnect, ping keepalive, staleness watchdog. On regaining a connection
// after a drop it fires onReconnect so the caller can re-bootstrap (query
// invalidation). Parsing happens in parse.ts via parseWsMessage.

import { env } from '../env';
import { parseWsMessage, type FeedDelta } from './parse';

const CHANNELS = [
  'market:updated',
  'event:update',
  'event:added',
  'event:removed',
] as const;
const PING_MS = 15_000;
const STALE_MS = 45_000;
const MAX_DELAY_MS = 16_000;

export interface WsCallbacks {
  onMessage: (delta: FeedDelta) => void;
  /** connection regained after a drop (or staleness-triggered reset) */
  onReconnect: () => void;
}

const toWsUrl = (httpUrl: string) =>
  httpUrl.replace(/^http/, 'ws').replace(/\/$/, '') + '/ws';

export function connectZenoWs({
  onMessage,
  onReconnect,
}: WsCallbacks): () => void {
  let ws: WebSocket | null = null;
  let attempts = 0;
  let hadSession = false;
  let dead = false;
  let lastMessageAt = Date.now();
  let heartbeat: ReturnType<typeof setInterval> | undefined;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  const open = () => {
    ws = new WebSocket(toWsUrl(env.zenoUrl));
    ws.onopen = () => {
      const isReconnect = hadSession;
      attempts = 0;
      hadSession = true;
      lastMessageAt = Date.now();
      for (const channel of CHANNELS) {
        ws?.send(JSON.stringify({ type: 'subscribe', channel }));
      }
      heartbeat = setInterval(() => {
        if (Date.now() - lastMessageAt > STALE_MS) {
          ws?.close(); // stale → onclose → reconnect → onReconnect re-bootstraps
          return;
        }
        ws?.send(JSON.stringify({ type: 'ping' }));
      }, PING_MS);
      if (isReconnect) onReconnect();
    };
    ws.onmessage = (ev: MessageEvent) => {
      lastMessageAt = Date.now();
      const delta = parseWsMessage(String(ev.data));
      if (delta) onMessage(delta);
    };
    ws.onclose = () => {
      clearInterval(heartbeat);
      if (dead) return;
      const delay = Math.min(1000 * 2 ** attempts++, MAX_DELAY_MS);
      reconnectTimer = setTimeout(open, delay);
    };
  };

  open();

  return () => {
    dead = true;
    clearInterval(heartbeat);
    clearTimeout(reconnectTimer);
    ws?.close();
  };
}
